export const OTP_POLICY = {
  length: 4,
  expiresInSeconds: 5 * 60,
  resendCooldownSeconds: 120,
  maxAttempts: 5
}

// Email verification code policy (6-digit, 10-minute window) — mirrors the
// backend EmailVerificationService::sendVerificationCode behaviour.
export const EMAIL_OTP_POLICY = {
  length: 6,
  expiresInSeconds: 10 * 60,
  resendCooldownSeconds: 120,
  maxAttempts: 5
}

const INDIA_COUNTRY_CODE = '91'

const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '')

export const normalizePhoneNumber = (value) => {
  const digits = onlyDigits(value)

  if (!digits) {
    return ''
  }

  if (digits.length === 10) {
    return `+${INDIA_COUNTRY_CODE}${digits}`
  }

  if (digits.length === 11 && digits.startsWith('0')) {
    return `+${INDIA_COUNTRY_CODE}${digits.slice(1)}`
  }

  if (digits.length === 12 && digits.startsWith(INDIA_COUNTRY_CODE)) {
    return `+${digits}`
  }

  return ''
}

/**
 * Extract the bare 10-digit mobile number (no country code prefix).
 */
export const extractMobileDigits = (value) => {
  const digits = onlyDigits(value)

  if (digits.length === 10) {
    return digits
  }

  if (digits.length === 11 && digits.startsWith('0')) {
    return digits.slice(1)
  }

  if (digits.length === 12 && digits.startsWith(INDIA_COUNTRY_CODE)) {
    return digits.slice(2)
  }

  if (digits.length === 13 && digits.startsWith('+' + INDIA_COUNTRY_CODE)) {
    return digits.slice(3)
  }

  // Return last 10 digits if longer, or raw digits if shorter
  return digits.slice(-10)
}

export const validatePhoneNumber = (value) => {
  const normalized = normalizePhoneNumber(value)

  if (!normalized) {
    return {
      valid: false,
      normalized: '',
      code: 'VALIDATION_ERROR',
      message: 'Enter a valid 10-digit mobile number.'
    }
  }

  return {
    valid: true,
    normalized,
    code: null,
    message: ''
  }
}

export const sanitizeOtpCode = (value) => onlyDigits(value).slice(0, OTP_POLICY.length)

export const validateOtpCode = (value) => {
  const otp = sanitizeOtpCode(value)

  if (otp.length !== OTP_POLICY.length) {
    return {
      valid: false,
      code: 'VALIDATION_ERROR',
      message: `Enter a ${OTP_POLICY.length}-digit OTP.`
    }
  }

  return {
    valid: true,
    code: null,
    message: '',
    normalized: otp
  }
}

// ─── Email verification code ───────────────────────────────────────────────

export const sanitizeEmailCode = (value) => onlyDigits(value).slice(0, EMAIL_OTP_POLICY.length)

export const validateEmailCode = (value) => {
  const code = sanitizeEmailCode(value)

  if (code.length !== EMAIL_OTP_POLICY.length) {
    return {
      valid: false,
      code: 'VALIDATION_ERROR',
      message: `Enter the ${EMAIL_OTP_POLICY.length}-digit code sent to your email.`
    }
  }

  return {
    valid: true,
    code: null,
    message: '',
    normalized: code
  }
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const validateEmail = (value) => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) {
    return { valid: false, message: 'Email is required.' }
  }
  if (!EMAIL_REGEX.test(trimmed)) {
    return { valid: false, message: 'Enter a valid email address.' }
  }
  return { valid: true, message: '', normalized: trimmed }
}

export const maskEmail = (value) => {
  const trimmed = String(value ?? '').trim()
  const at = trimmed.indexOf('@')
  if (at <= 0) return trimmed
  const name = trimmed.slice(0, at)
  const domain = trimmed.slice(at)
  if (name.length <= 2) return `${name[0] || ''}***${domain}`
  return `${name.slice(0, 2)}***${domain}`
}

const PASSWORD_MIN_LENGTH = 6

export const validatePassword = (value) => {
  const password = String(value ?? '')

  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      valid: false,
      message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`
    }
  }

  if (!/[A-Z]/.test(password)) {
    return {
      valid: false,
      message: 'Password must contain at least one uppercase letter.'
    }
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password)) {
    return {
      valid: false,
      message: 'Password must contain at least one special character.'
    }
  }

  return { valid: true, message: '' }
}

export const validateSignupFields = ({ fullName, mobile, email, password, passwordConfirmation }) => {
  const errors = {}

  const trimmedName = String(fullName ?? '').trim()
  if (trimmedName.length < 2) {
    errors.fullName = 'Name must be at least 2 characters.'
  }

  const mobileDigits = onlyDigits(mobile)
  if (mobileDigits.length !== 10) {
    errors.mobile = 'Enter a valid 10-digit mobile number.'
  }

  // Email is now REQUIRED — we verify it with a code so notification emails
  // can be delivered.
  const emailResult = validateEmail(email)
  if (!emailResult.valid) {
    errors.email = emailResult.message
  }

  const passwordResult = validatePassword(password)
  if (!passwordResult.valid) {
    errors.password = passwordResult.message
  }

  if (password !== passwordConfirmation) {
    errors.passwordConfirmation = 'Passwords do not match.'
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  }
}

export const validateForgotPasswordFields = ({ otp, password, passwordConfirmation }) => {
  const errors = {}

  const otpResult = validateOtpCode(otp)
  if (!otpResult.valid) {
    errors.otp = otpResult.message
  }

  const passwordResult = validatePassword(password)
  if (!passwordResult.valid) {
    errors.password = passwordResult.message
  }

  if (password !== passwordConfirmation) {
    errors.passwordConfirmation = 'Passwords do not match.'
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  }
}

export const maskPhoneNumber = (phone) => {
  const normalized = normalizePhoneNumber(phone)
  if (!normalized) {
    return ''
  }

  const digits = normalized.replace(/^\+91/, '')
  if (digits.length !== 10) {
    return normalized
  }

  return `+91 ${digits.slice(0, 2)}****${digits.slice(6)}`
}

export const isSafeRedirectPath = (value) => {
  if (typeof value !== 'string') {
    return false
  }

  if (!value.startsWith('/')) {
    return false
  }

  if (value.startsWith('//')) {
    return false
  }

  return true
}
